import { Component } from '@angular/core';
import { ApiService } from 'src/app/shared/services/api.service';
import { IData, Column, Table, Enum, ChangesetView } from "../../shared/data/engineering-data";
import { ColumnType, MeasurementContextDescriptions, UnitTypeDescriptions } from 'src/app/shared/data/engineering-enums';
import { first } from 'rxjs';

@Component({
  selector: 'engineering-data',
  templateUrl: './engineering-data.component.html',
  styleUrl: './engineering-data.component.scss',
  standalone: false
})
export class EngineeringDataComponent {
  constructor(
    private api: ApiService
  ) { }

  ngOnInit() {
    this.initializeData();
  }

  mode: string = "tables";
  tables: Table[] = [];
  table: Table;
  columns: Column[] = [];
  changesets: ChangesetView[] = [];
  comparableChangesets: ChangesetView[] = [];
  currentChangeset: ChangesetView;
  compareChangeset: ChangesetView;
  originalData: IData[] = [];
  currentData: IData[] = [];
  enums: Enum[] = [];
  showDisabled: boolean = false;

  onTableChange(table: Table) {
    this.table = table;
    this.loadTableData();
  }

  onChangesetChange(changeset: ChangesetView) {
    this.compareChangeset = changeset;
    this.comparableChangesets = this.changesets.filter(cs => cs.TableChangesetId <= changeset.TableChangesetId);
    this.loadTableDataFromChangeset(this.currentChangeset, false);
  }

  onCompareChange(changeset: ChangesetView) {
    this.loadTableDataFromChangeset(this.compareChangeset, true);
  }

  initializeData() {
    this.api.EngineeringData.GetEnums()
      .subscribe(x => {
        this.enums = x;
      });
    this.api.EngineeringData.GetTableList()
      .subscribe(x => {
        this.tables = x;
        this.table = x[0];
        this.loadTableData();
      });
  }

  loadTableData() {
    this.originalData = [];
    this.currentData = [];
    this.columns = [];
    this.changesets = [];
    this.api.EngineeringData.GetTableColumns(this.table.Id)
      .subscribe(cols => {
        this.columns = cols;
      });
    this.api.EngineeringData.GetChangesets(this.table.Id)
      .subscribe(x => {
        this.changesets = this.comparableChangesets = x;
        this.currentChangeset = this.changesets[this.changesets.length - 1];
        this.compareChangeset = this.currentChangeset;
        this.loadTableDataFromChangeset(this.currentChangeset, false);
      });
  }

  loadTableDataFromChangeset(changeset: ChangesetView, isCompare: boolean) {
    this.api.EngineeringData.GetTableData(this.table.Id, changeset.TableChangesetId)
      .subscribe(x => {
        this.originalData = x;
        if (!isCompare) {
          this.currentData = structuredClone(this.originalData);
        }
      });
  }

  editRow: any;
  editColumn: Column;
  editValue: any;
  editEnumValues: Enum[];

  onCellClick(row: any, column: Column) {
    this.editRow = row;
    this.editColumn = column;
    this.editValue = row[column.Name];
    this.editEnumValues = this.getColumnEnumValues(column);
  }

  getColumnEnumValues(column: Column): Enum[] {
    return this.enums.filter(e => e.Type === column.EnumType);
  }

  isEditingCell(row: any, column: Column): boolean {
    return this.editRow === row && this.editColumn === column;
  }

  isVisible(row: any, column: Column): boolean {
    return this.showDisabled || row.IsEnabled;
  }

  cellChanged() {
    this.editRow[this.editColumn.Name] = this.editValue;
  }

  isDataModified(row: any, columnName: string): boolean {
    let rowIndex = this.originalData.findIndex(d => d.DataId === row.DataId);
    if (rowIndex === -1) return false;
    let originalRow = <any>this.originalData[rowIndex];
    return originalRow[columnName] != row[columnName];
  }

  isDataNew(row: any): boolean {
    return this.originalData.findIndex(d => d.DataId === row.DataId) === -1;
  }

  onDragStart(event: DragEvent, row: any) {
    event.dataTransfer?.setData('text/plain', JSON.stringify(row));
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onDrop(event: DragEvent, row: any) {
    event.preventDefault();
    const draggedRow = JSON.parse(event.dataTransfer?.getData('text/plain') || '{}');
    this.moveBefore(draggedRow, row);
  }

  moveBefore(newRow: any, referenceRow: any) {
    const index = this.currentData.findIndex(r => r.DataId === referenceRow.DataId);
    if (index !== -1) {
      const draggedRowIndex = this.currentData.findIndex(r => r.DataId === newRow.DataId);
      if (draggedRowIndex !== -1) {
        this.currentData.splice(draggedRowIndex, 1);
      }
      this.currentData.splice(index, 0, newRow);
      this.currentData.forEach((row, i) => row.SortOrder = i + 1);
    }
  }

  addNewRow() {
    let newRow: any = { Id: 0, DataId: 0, IsEnabled: true, FirstChangesetId: 1, LastChangesetId: 1 };
    newRow.DataId = this.currentData.length > 0 ? Math.max(...this.currentData.map(r => r.DataId)) + 1 : 1;
    newRow.SortOrder = newRow.DataId;
    this.columns.forEach(col => {
      newRow[col.Name] = null;
    });
    this.currentData.push(newRow);
  }

  deleteRow(row: any) {
    if (confirm('Are you sure you want to delete this row?')) {
      const index = this.currentData.findIndex(r => r.DataId === row.DataId);
      if (index !== -1) {
        this.currentData.splice(index, 1);
        this.currentData.forEach((row, i) => row.SortOrder = i + 1);
      }
    }
  }

  pasteAndFillFromClipboard() {
    navigator.clipboard.readText().then(text => {
      const rows = text.split('\n').map(row => row.split('\t'));
      // Assuming the first row is the header
      const headers = rows[0];
      for (let i = 1; i < rows.length; i++) {
        const newRow: any = {};
        rows[i].forEach((cell: string, index: number) => {
          newRow[headers[index]] = cell;
        });
        this.currentData.push(newRow);
      }
    });
  }

  saveChanges() {
    let modifiedData: IData[] = [];
    const newData = this.currentData.filter(row => this.isDataNew(row));
    const standardModifiedData = this.currentData.filter(row =>
      this.isDataModified(row, "IsEnabled") ||
      this.isDataModified(row, "SortOrder"));
    modifiedData = [...newData, ...standardModifiedData];
    this.columns.forEach(column => {
      const columnModifiedData = this.currentData.filter(row => this.isDataModified(row, column.Name));
      columnModifiedData.forEach(element => {
        if (!modifiedData.includes(element)) {
          modifiedData.push(element);
        }
      });
    });

    if (modifiedData.length > 0) {
      var unmodifiedData = this.currentData.filter(row => !modifiedData.includes(row));
      var unmodifiedIds = unmodifiedData.map(row => row.Id);
      if (confirm("Are you sure you want to create a new Changeset with " + unmodifiedData.length + " modified rows?")) {
        this.api.EngineeringData.UpdateEngineeringData(this.table.Id, modifiedData, unmodifiedIds)
          .subscribe(() => {
            this.loadTableData();
          });
      }
    }
    else {
      alert('No changes to save.');
    }
  }

  getDisplayHeading(column: Column): string {
    let displayName = column.Name;
    if (<ColumnType>column.TypeId == ColumnType.Enum)
      displayName = displayName.slice(0, displayName.length - 2);
    return this.variableToDisplay(displayName);
  }

  variableToDisplay(str: string): string {
    return str.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  }

  getTypeString(column: Column): string {
    return ColumnType[column.TypeId];
  }

  getContextString(column: Column): string {
    return column.ContextId ? MeasurementContextDescriptions.get(column.ContextId) ?? '' : '';
  }

  getUnitString(column: Column): string {
    return column.UnitId ? UnitTypeDescriptions.get(column.UnitId) ?? '' : '';
  }

  getDisplayValue(row: any, column: Column): string {
    const value = (<any>row)[column.Name];
    
    switch (<ColumnType>column.TypeId) {
      case ColumnType.String:
      case ColumnType.Boolean:
      case ColumnType.DateTime:
        return value !== undefined && value !== null ? value.toString() : '';
      case ColumnType.Int:
        return value !== undefined && value !== null ? value.toString() : '';
      case ColumnType.Double:
      case ColumnType.Decimal:
        var suffix = '';
        if (column.UnitId) {
          suffix = UnitTypeDescriptions.get(column.UnitId) || '';
        }
        return value !== undefined && value !== null ? Number(value).toFixed(column.DecimalDigits) + (suffix ? ' ' + suffix : '') : '';
      case ColumnType.Enum:
        return this.enums.find(e => e.Type === column.EnumType && e.CodeId === value)?.Label || '';
      default:
        return value !== undefined && value !== null ? value.toString() : '';
    }
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const utcDateString = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
    const date = new Date(utcDateString);
    if (date.getFullYear() < 1980 || !date.valueOf()) return 'N/A';
    return date.toLocaleString('en-US', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  }
}
